import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "@/lib/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/gallery", label: "Gallery" },
  { to: "/horoscope", label: "Horoscope" },
  { to: "/numerology", label: "Numerology" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? "glass-nav" : "bg-transparent"}`}
        data-testid="main-navbar"
      >
        <nav className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between h-[74px]">
          <Link to="/" className="flex shrink-0 items-center group" data-testid="nav-logo">
            <img src={BRAND.logo} alt="ENRICH VASTU" className="h-[68px] w-[132px] object-contain sm:w-[150px]" />
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={`text-sm tracking-wide link-underline transition-colors ${loc.pathname === n.to ? "text-gold" : "text-[#F8FAFC]/80 hover:text-white"}`}
              >
                {n.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-book"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-primary-green px-5 py-2.5 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#059669]"
            >
              <Phone size={15} /> Book Consultation
            </Link>
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="fixed top-[74px] inset-x-0 z-40 glass-nav lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {[...NAV, { to: "/contact", label: "Contact" }].map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-link-${n.label.toLowerCase()}`}
                  className="py-3 border-b border-white/5 text-lg font-display text-[#F8FAFC]/90"
                >
                  {n.label}
                </Link>
              ))}
              <Link to="/contact" className="mt-4 rounded-full bg-primary-green px-5 py-3 text-center text-white font-medium">
                Book Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
